import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { BookmarkSimple, CaretDown, Check, X } from "@phosphor-icons/react";
import { Popover } from "radix-ui";
import type { Decision, Job } from "../../shared/types.ts";
import { List, Monogram, ownsKeys, SourceLogo, StatusBadge, cn, softSpring, spring } from "../ui/index.ts";
import { useMedia } from "../ui/useMedia.ts";
import { ago, pay, place, SENIORITY, visaNote, WORKPLACE } from "../lib/format.ts";
import { Detail, DetailBody } from "./Detail.tsx";
import { ghostRisk, MatchChip, SendButton } from "./parts.tsx";

export type Act = (d: Decision, job: Job, from?: Element | null) => void;

export interface BoardProps {
  items: Job[];
  /** Ids that stagger in when they mount: the first page of a search. Everything else appears in place. */
  fresh: Set<string>;
  mark: (id: string) => Decision | null;
  onAct: Act;
  /** Records an apply Headcount sends from a row's button; true once the server took it. */
  onSend: (job: Job, from: Element) => Promise<boolean>;
  /** The row's check has shown: take it out. */
  onSent: (job: Job) => void;
  /** The detail pane sent or took back an apply itself. */
  onApplied: (job: Job, on: boolean) => void;
  stamp?: (job: Job) => string;
  dim?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
  /** Filters in a column left of the list, on screens wide enough for three columns. */
  rail?: ReactNode;
  /** Unsaving takes the row out of this list, so the selection moves on as it does for pass and apply. */
  dropOnUnsave?: boolean;
}

/** The job list with a detail pane beside it on wide screens, and a detail sheet on phones. */
export function Board({ items, fresh, mark, onAct, onSend, onSent, onApplied, stamp, dim, header, footer, rail, dropOnUnsave = false }: BoardProps) {
  const wide = useMedia("(min-width: 1024px)");
  const xl = useMedia("(min-width: 1280px)");
  const [sel, setSel] = useState<string | null>(null);
  const [open, setOpen] = useState<Job | null>(null);
  const rows = useRef(new Map<string, HTMLElement>());
  const group = useId();
  const frame = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);
  const current = items.find((j) => j.id === sel) ?? items[0] ?? null;
  const withRail = xl && !!rail;
  const applies = (job: Job) => job.canAutoApply && mark(job.id) !== "apply";

  const select = (job: Job | undefined, focus = false) => {
    if (!job) return;
    setSel(job.id);
    const el = rows.current.get(job.id);
    if (focus && el) {
      el.scrollIntoView({ block: "nearest" });
      el.querySelector<HTMLButtonElement>("button[data-row]")?.focus({ preventScroll: true });
    }
  };

  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    const measure = () => setTop(Math.round(el.getBoundingClientRect().top + scrollY));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [wide]);

  useEffect(() => {
    if (!wide) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || ownsKeys(e.target) || !current) return;
      if (e.key === "a") {
        if (e.repeat || !applies(current)) return;
        e.preventDefault();
        rows.current.get(current.id)?.querySelector<HTMLButtonElement>("[data-apply]")?.click();
        return;
      }
      const step = e.key === "j" || e.key === "ArrowDown" ? 1 : e.key === "k" || e.key === "ArrowUp" ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const i = items.findIndex((j) => j.id === current.id);
      select(items[Math.min(items.length - 1, Math.max(0, i + step))], true);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  });

  /** Moves the selection on and closes the sheet for a row about to drop. */
  const moveOff = (job: Job) => {
    if (job.id === current?.id) {
      const i = items.findIndex((j) => j.id === job.id);
      setSel(items[i + 1]?.id ?? items[i - 1]?.id ?? null);
    }
    if (open?.id === job.id) setOpen(null);
  };

  const act: Act = (d, job, from) => {
    if (d !== "save" || (dropOnUnsave && mark(job.id) === "save")) moveOff(job);
    onAct(d, job, from);
  };

  const rowsList = (
    <List className={cn("transition-[opacity] duration-200", dim && "opacity-55")}>
      <AnimatePresence initial={false} mode="popLayout">
        {items.map((j, i) => (
          <Row
            key={j.id}
            ref={(el) => {
              if (el) rows.current.set(j.id, el);
              else rows.current.delete(j.id);
            }}
            job={j}
            group={group}
            stagger={fresh.has(j.id) ? Math.min(i, 12) : -1}
            selected={wide && current?.id === j.id}
            saved={mark(j.id) === "save"}
            stamp={stamp?.(j)}
            onOpen={() => (wide ? select(j) : setOpen(j))}
            onSave={(el) => act("save", j, el)}
            onSend={applies(j) ? (el) => onSend(j, el) : undefined}
            onSent={() => {
              moveOff(j);
              onSent(j);
            }}
          />
        ))}
      </AnimatePresence>
    </List>
  );

  if (!wide)
    return (
      <div className="flex min-w-0 flex-col gap-3">
        {header}
        {rowsList}
        {footer}
        <Detail job={open} onClose={() => setOpen(null)} onAct={(d, j) => act(d, j)} onApplied={onApplied} mark={open ? mark(open.id) : null} />
      </div>
    );

  return (
    <div
      ref={frame}
      style={{ height: `calc(100dvh - ${top}px - 1.5rem)` }}
      className={cn(
        "-mb-10 grid min-h-[520px] gap-5",
        withRail ? "grid-cols-[200px_minmax(0,1fr)_minmax(0,1.25fr)]" : "grid-cols-[minmax(0,400px)_minmax(0,1fr)]",
      )}
    >
      {withRail && (
        <aside aria-label="Filters" className="-mx-1 min-h-0 overflow-y-auto overscroll-contain px-1 pb-6 [scrollbar-width:thin]">
          {rail}
        </aside>
      )}
      <div className="flex min-h-0 min-w-0 flex-col gap-3">
        {header}
        <motion.div layoutScroll data-board-scroll className="-mx-1 min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 pb-6 pt-0.5 [scrollbar-width:thin]">
          {rowsList}
          {footer}
        </motion.div>
      </div>
      <aside aria-label="Job details" className="relative min-h-0 overflow-hidden rounded-card bg-surface shadow-card">
        <AnimatePresence initial={false}>
          {current && (
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0, transition: { ...softSpring, opacity: { duration: 0.18 } } }}
              exit={{ opacity: 0, transition: { duration: 0.12 } }}
              className="absolute inset-0 overflow-y-auto overscroll-contain p-5"
            >
              <DetailBody job={current} onAct={(d, j) => act(d, j)} onApplied={onApplied} mark={mark(current.id)} />
            </motion.div>
          )}
        </AnimatePresence>
      </aside>
    </div>
  );
}

function Row({
  job,
  group,
  stagger,
  selected,
  saved,
  stamp,
  onOpen,
  onSave,
  onSend,
  onSent,
  ref,
}: {
  job: Job;
  group: string;
  stagger: number;
  selected: boolean;
  saved: boolean;
  stamp?: string;
  onOpen: () => void;
  onSave: (el: Element) => void;
  /** Auto-apply jobs send from the row, without opening the role. */
  onSend?: (from: HTMLButtonElement) => Promise<boolean>;
  onSent: () => void;
  ref: (el: HTMLLIElement | null) => void;
}) {
  const money = pay(job);
  const wp = WORKPLACE[job.workplace];
  const where = place(job);
  const when = stamp ?? ago(job.postedAt);
  const level = job.seniority ? SENIORITY[job.seniority] : undefined;
  const visa = visaNote(job.visa);
  const ghost = ghostRisk(job.ghost);
  return (
    <motion.li
      ref={ref}
      layout="position"
      initial={stagger >= 0 ? { opacity: 0, y: 10 } : false}
      animate={{ opacity: 1, y: 0, transition: { ...softSpring, delay: stagger >= 0 ? stagger * 0.03 : 0 } }}
      exit={{ opacity: 0, x: -24, transition: { duration: 0.18 } }}
      transition={spring}
      className="group relative scroll-my-2 transition-[background-color] duration-150 hover:bg-sunk/70"
    >
      {selected && (
        <motion.span layoutId={`row-${group}`} transition={spring} className="absolute inset-0 bg-primary-soft" aria-hidden />
      )}
      <button
        type="button"
        data-row
        onClick={onOpen}
        aria-current={selected || undefined}
        aria-label={`${job.title} at ${job.company}`}
        className="absolute inset-0 z-[1] focus-visible:outline-2 focus-visible:-outline-offset-2"
      />
      <div className="pointer-events-none relative flex gap-3 py-3 pl-4 pr-12">
        <Monogram name={job.company} url={job.jobUrl} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold leading-snug tracking-[-0.01em] text-ink" title={job.title}>
            {job.title}
          </p>
          <div className="mt-0.5 flex items-baseline gap-3 text-[13px] text-muted">
            <p className="min-w-0 flex-1 truncate">
              <span className="font-semibold text-ink-soft">{job.company}</span>
              {` · ${where}`}
              {wp && where !== wp && ` · ${wp}`}
            </p>
            <span className="flex shrink-0 items-center gap-1.5 self-center text-[12px] tabular-nums">
              <SourceLogo source={job.source} size="xs" className="size-3.5" />
              {when}
            </span>
          </div>
          <div className="-mr-8 mt-2 flex flex-wrap items-center gap-1.5">
            {money && <span className="mr-0.5 text-[13px] font-bold text-ink tabular-nums">{money}</span>}
            {job.canAutoApply && !onSend && <StatusBadge tone="accent">Auto-apply</StatusBadge>}
            <MatchChip score={job.score} />
            {level && <StatusBadge tone="gray">{level}</StatusBadge>}
            {visa?.tone === "ok" && <StatusBadge tone="mint">Sponsors visas</StatusBadge>}
            {ghost?.label === "High" && <StatusBadge tone="red">Ghost risk</StatusBadge>}
            {onSend && (
              <SendButton
                size="sm"
                data-apply
                aria-label={`Apply to ${job.title}`}
                send={onSend}
                onSent={onSent}
                className="pointer-events-auto relative z-[2] -my-1 ml-auto"
              />
            )}
          </div>
        </div>
      </div>
      <SaveToggle saved={saved} onToggle={onSave} label={job.title} className="absolute right-2 top-2 z-[2]" />
    </motion.li>
  );
}

function SaveToggle({ saved, onToggle, label, className }: { saved: boolean; onToggle: (el: Element) => void; label: string; className?: string }) {
  return (
    <motion.button
      type="button"
      aria-pressed={saved}
      aria-label={saved ? `Unsave ${label}` : `Save ${label}`}
      title={saved ? "Saved" : "Save"}
      onClick={(e) => onToggle(e.currentTarget)}
      whileTap={{ scale: 0.82 }}
      transition={spring}
      className={cn(
        "grid size-8 place-items-center rounded-control transition-[background-color,color] duration-150",
        saved ? "text-primary hover:bg-primary-soft" : "text-muted hover:bg-ink/[0.06] hover:text-ink",
        className,
      )}
    >
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          key={saved ? "on" : "off"}
          initial={{ scale: 0.3, opacity: 0, rotate: saved ? -30 : 0 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          exit={{ scale: 0.3, opacity: 0 }}
          transition={{ type: "spring", stiffness: 600, damping: 22 }}
          className="grid place-items-center"
        >
          <BookmarkSimple size={17} weight={saved ? "fill" : "bold"} aria-hidden />
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
}

const pill = "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-control text-sm font-semibold transition-[background-color,color,box-shadow] duration-150";
const idle =
  "bg-surface text-ink-soft ring-1 ring-line-strong hover:bg-sunk hover:text-ink data-[state=open]:ring-2 data-[state=open]:ring-primary";
const filled = "bg-primary-soft text-primary-ink ring-1 ring-primary/50";

/** A filter pill that toggles on and off. */
export function TogglePill({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <motion.button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      whileTap={{ scale: 0.94 }}
      transition={spring}
      className={cn(pill, "px-3.5", on ? filled : idle)}
    >
      {children}
      <AnimatePresence initial={false}>
        {on && (
          <motion.span
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={spring}
            className="-mr-1 overflow-hidden"
          >
            <X size={14} weight="bold" className="ml-0.5" aria-hidden />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

/**
 * A filter pill with a popover of options. `multi` keeps the popover open and toggles each option; otherwise a pick
 * closes it. Once set, the pill fills and grows a clear button.
 */
export function PickFilter({
  label,
  value,
  options,
  onChange,
  multi = false,
}: {
  label: string;
  value: string[];
  options: Record<string, string>;
  onChange: (v: string[]) => void;
  multi?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const on = value.length > 0;
  const first = value[0] ? options[value[0]] : undefined;
  const text = !on ? label : value.length === 1 ? first : `${first} +${value.length - 1}`;
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <motion.span
        whileTap={{ scale: 0.95 }}
        transition={spring}
        className={cn(pill, "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary", on ? filled : idle)}
        data-state={open ? "open" : "closed"}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn("inline-flex h-full items-center gap-1 rounded-control focus-visible:outline-none", on ? "pl-3.5 pr-1" : "pl-3.5 pr-2.5")}
          >
            <span className="max-w-40 truncate">{text}</span>
            {!on && <CaretDown size={14} weight="bold" className={cn("transition-[rotate] duration-200", open && "rotate-180")} aria-hidden />}
          </button>
        </Popover.Trigger>
        {on && (
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => onChange([])}
            className="mr-1 grid size-7 place-items-center rounded-chip transition-[background-color] duration-150 hover:bg-primary/10 focus-visible:outline-none"
          >
            <X size={14} weight="bold" aria-hidden />
          </button>
        )}
      </motion.span>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={8} collisionPadding={12} className="z-50 outline-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={spring}
            style={{ transformOrigin: "var(--radix-popover-content-transform-origin)" }}
            className="flex max-h-[min(22rem,var(--radix-popover-content-available-height))] min-w-56 flex-col overflow-y-auto rounded-card bg-surface p-1 shadow-raised"
          >
            <Options value={value} options={options} onChange={onChange} multi={multi} onPicked={() => !multi && setOpen(false)} />
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Checkbox or radio rows for one filter, shared by the popover pills and the filter rail. */
export function Options({
  value,
  options,
  onChange,
  multi = false,
  onPicked,
  render,
  compact = false,
}: {
  value: string[];
  options: Record<string, string>;
  onChange: (v: string[]) => void;
  multi?: boolean;
  onPicked?: () => void;
  render?: (key: string, label: string) => ReactNode;
  compact?: boolean;
}) {
  const pick = (k: string) => {
    if (!multi) onChange(value[0] === k ? [] : [k]);
    else onChange(value.includes(k) ? value.filter((v) => v !== k) : [...value, k]);
    onPicked?.();
  };
  return (
    <>
      {Object.entries(options).map(([k, v]) => {
        const picked = value.includes(k);
        return (
          <button
            key={k}
            type="button"
            role={multi ? "menuitemcheckbox" : "menuitemradio"}
            aria-checked={picked}
            onClick={() => pick(k)}
            className={cn(
              "flex items-center gap-2.5 rounded-control text-left transition-[background-color,color] duration-100",
              compact ? "min-h-8 px-2 text-[13.5px]" : "min-h-10 px-3 text-[15px]",
              picked ? "font-semibold text-ink" : "text-ink-soft hover:bg-sunk hover:text-ink",
            )}
          >
            <span
              className={cn(
                "grid size-4 shrink-0 place-items-center transition-[background-color,box-shadow] duration-150",
                multi ? "rounded-sm" : "rounded-full",
                picked ? "bg-primary text-on-primary" : "bg-surface ring-1 ring-line-strong",
              )}
            >
              <AnimatePresence initial={false}>
                {picked && (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={spring} className="grid place-items-center">
                    {multi ? <Check size={12} weight="bold" aria-hidden /> : <span className="size-1.5 rounded-full bg-current" />}
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
            <span className="min-w-0 flex-1 truncate">{render ? render(k, v) : v}</span>
          </button>
        );
      })}
    </>
  );
}
